import type { BudgetTypes } from 'src/App/pages/ad-budget-selection/mapBudgetOption';
import {
  mapAdBudgetType,
  mapPricingModel,
  mapAdBudgetTypeToTrackingBudgetType,
} from 'src/App/utils/analytics/event';

import type { PricingModel } from '../../../generated/graphql-types';
import { centsToDollars } from '../../../utils/price';
import type { AdBudgetInfo } from '../context/reducer';

interface CheckOutPageBaseInput {
  pricingModel?: PricingModel | null;
  productSetId: string;
  productEvents: string;
  adBudgetList?: AdBudgetInfo[];
  paymentMethodShown?: string[];
}
interface ContractProductPriceQuery {
  position: number;
  id: string;
  price: number;
  currency: string;
  type: BudgetTypes;
}
interface CheckOutPageBaseEvent {
  actionOrigin: string;
  advertiserTypePricing?: string;
  pricingType?: string;
  cajaFlowType: string;
  productCategories: string[];
  currentPage: string;
  productEvents: string;
  contractProductSetId: string;
  contractProductPriceQuery: ContractProductPriceQuery[];
  contractProductSetProductId: string;
  contractProductName: string;
  productIds: string[];
  productTotalPrice: number[];
  paymentMethodShown?: string[];
}

interface CheckOutPayByInvoicePressedEvent extends CheckOutPageBaseEvent {
  adBudgetPrices: number[];
  adBudgetType: string;
}

type CheckoutActionEvent = CheckOutPageBaseEvent;

interface ConfirmationActionEvent extends CheckOutPageBaseEvent {
  adBudgetPrices: number[];
  adBudgetType: string;
  paymentMethod: string;
}

type ConfirmationOnLoadEvent = CheckOutPageBaseEvent & {
  paymentMethod: string;
  adBudgetType: string;
  isSaveCreditCardChecked: boolean;
  isPaidByStoredCreditCard: boolean;
  purchasePaymentReferenceNumber: string;
};

const mapAdBudgetsListToTrackingPayload = (
  adBudgets: AdBudgetInfo[],
): ContractProductPriceQuery[] =>
  adBudgets.map((adBudget, position) => ({
    position: position + 1,
    id: adBudget.id,
    price: adBudget.price,
    currency: adBudget.currency,
    type: mapAdBudgetTypeToTrackingBudgetType(adBudget.ads),
  }));

const createCheckoutPageOnLoadBaseEvent = (
  {
    pricingModel,
    productSetId,
    productEvents,
    adBudgetList,
    paymentMethodShown,
  }: CheckOutPageBaseInput,
  selectedBudget: AdBudgetInfo,
  currentPage: string = 'seek contract checkout',
): CheckOutPageBaseEvent => ({
  actionOrigin: 'products discounts',
  cajaFlowType: `${mapAdBudgetType(selectedBudget.ads)}|${centsToDollars(
    selectedBudget.price,
  )}`,
  pricingType: mapPricingModel(pricingModel ?? 'HORIZON'),
  productCategories: ['contract'],
  productEvents,
  productIds: [mapAdBudgetType(selectedBudget.ads)],
  contractProductSetId: productSetId,
  contractProductPriceQuery: adBudgetList
    ? mapAdBudgetsListToTrackingPayload(adBudgetList)
    : [],
  contractProductSetProductId: selectedBudget.id,
  contractProductName: selectedBudget.name,
  productTotalPrice: [centsToDollars(selectedBudget.price)],
  ...(paymentMethodShown && { paymentMethodShown }),
  currentPage,
});

export {
  type CheckOutPageBaseEvent,
  type CheckOutPageBaseInput,
  type CheckoutActionEvent,
  type ConfirmationOnLoadEvent,
  type ConfirmationActionEvent,
  type CheckOutPayByInvoicePressedEvent,
  createCheckoutPageOnLoadBaseEvent,
};
